import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button, DatePicker, Input } from 'antd';
import moment from 'moment';
import { Box } from 'atoms';
import { getWarehousePortfolioV2 } from 'store/warehousingSliceV2';
import { WAREHOUSING_ROUTE, normalizePortfolio, withCustomerView } from './warehousing.constants';
import WarehouseKpiRow from './components/WarehouseKpiRow';
import WarehouseProjectsTable from './components/WarehouseProjectsTable';
import {
	EmptyState,
	ErrorState,
	FilterRow,
	HeaderActions,
	HeaderLeft,
	PageContainer,
	PageHeader,
	PageSubtitle,
	PageTitle,
	SectionCard,
	SectionHeader,
	SectionSubtitle,
	SectionTitle,
} from './components/warehousingStyles';

const { Search } = Input;

export default function WarehousingDashboardPageV2() {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const [periodMonth, setPeriodMonth] = useState(() => moment().startOf('month'));
	const [payload, setPayload] = useState(null);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(false);
	const [search, setSearch] = useState('');

	const periodParam = useMemo(() => periodMonth.format('YYYY-MM-DD'), [periodMonth]);

	const fetchPortfolio = useCallback(async () => {
		setLoading(true);
		setLoadError(false);
		try {
			const result = await dispatch(getWarehousePortfolioV2({ period_month: periodParam }));
			setPayload(result);
		} catch (error) {
			setLoadError(true);
		} finally {
			setLoading(false);
		}
	}, [dispatch, periodParam]);

	useEffect(() => {
		fetchPortfolio();
	}, [fetchPortfolio]);

	const projects = useMemo(() => withCustomerView(payload?.projects), [payload]);
	const summary = useMemo(() => normalizePortfolio(payload), [payload]);

	// The endpoint accepts no search params, so filtering is client-side over projects[]
	const filteredProjects = useMemo(() => {
		const term = search.trim().toLowerCase();
		if (!term) return projects;
		return projects.filter(
			(project) =>
				(project.project_name || '').toLowerCase().includes(term) ||
				(project.project_code || '').toLowerCase().includes(term)
		);
	}, [projects, search]);

	const handleRowClick = (record) => {
		if (record?.project_id) navigate(`${WAREHOUSING_ROUTE}/${record.project_id}`);
	};

	const renderTableBody = () => {
		if (loadError) {
			return (
				<ErrorState>
					<Box mb="12px">Could not load the warehousing portfolio.</Box>
					<Button onClick={fetchPortfolio}>Retry</Button>
				</ErrorState>
			);
		}

		if (!loading && projects.length === 0) {
			return (
				<EmptyState>
					<div className="empty-title">No warehouse projects for this month</div>
					<div className="empty-body">
						Create a project with type Warehousing to get started.
					</div>
					<Button type="primary" onClick={() => navigate('/projects/add')}>
						Create Project
					</Button>
				</EmptyState>
			);
		}

		if (!loading && filteredProjects.length === 0 && search) {
			return (
				<EmptyState>
					<div className="empty-title">No projects match this search</div>
					<div className="empty-body">Try a different project name or code.</div>
				</EmptyState>
			);
		}

		return (
			<WarehouseProjectsTable
				projects={filteredProjects}
				loading={loading}
				onRowClick={handleRowClick}
			/>
		);
	};

	const projectCount = summary?.projectCount ?? projects.length;

	return (
		<PageContainer>
			<PageHeader>
				<HeaderLeft>
					<Box>
						<PageTitle>Warehousing</PageTitle>
						<PageSubtitle>
							{loading && !payload
								? 'Loading month-to-date performance…'
								: `Month-to-date performance — ${periodMonth.format(
										'MMMM YYYY'
								  )} · ${projectCount} active project${
										projectCount === 1 ? '' : 's'
								  }`}
						</PageSubtitle>
					</Box>
				</HeaderLeft>

				<HeaderActions>
					<DatePicker
						picker="month"
						allowClear={false}
						value={periodMonth}
						disabledDate={(current) => current && current > moment().endOf('month')}
						onChange={(value) => value && setPeriodMonth(value.startOf('month'))}
					/>
				</HeaderActions>
			</PageHeader>

			<WarehouseKpiRow summary={summary} loading={loading && !payload} />

			<SectionCard>
				<SectionHeader>
					<Box>
						<SectionTitle>Warehouse Projects</SectionTitle>
						<SectionSubtitle>
							Select a project to view fixed, daily and monthly detail.
						</SectionSubtitle>
					</Box>

					{projects.length > 0 && (
						<FilterRow>
							<Search
								allowClear
								placeholder="Search project name or code"
								style={{ width: 260 }}
								value={search}
								onChange={(e) => setSearch(e.target.value)}
							/>
						</FilterRow>
					)}
				</SectionHeader>

				{renderTableBody()}
			</SectionCard>
		</PageContainer>
	);
}
